import { MouseEvent, useMemo, useRef, useState } from "react";
import CopyToClipboardIcon from "./../../../assets/icons/copy.svg?react";
import ArrowDownCircleIcon from "./../../../assets/icons/arrow-down-circle.svg?react";
import ArrowTopRightOnSquareIcon from "./../../../assets/icons/arrow-top-right-on-square.svg?react";
import ArchiveBoxIcon from "./../../../assets/icons/archive-box.svg?react";
import FileIcon, { extToType } from "./../../general/FileIcon";
import { humanizeSize } from "../../../utils/file";
import { IpfsFile } from "../../../types/ipfs-file";
import { useSearch } from "../../../providers/SearchProvider";
import { FileTypes } from "../../../types";
import { Button, Flex, SimpleTable, Tooltip, Typography } from "djuno-design";
import HighlighterText from "../../general/HighlighterText";
import { DeleteModal } from "../../modals/QuestionModal";

export type FileProps = {
  name: string;
  size: number;
  file: IpfsFile;
  downloadLoading?: boolean;
  exportLoading?: boolean;
  deleteLoading?: boolean;
  onOpenFile?: (
    file: IpfsFile,
    e: MouseEvent<HTMLDivElement>,
    type: FileTypes | undefined
  ) => void;
  onDownload?: (file: IpfsFile) => Promise<any>;
  onExport?: (file: IpfsFile) => Promise<any>;
  onCopy?: (file: IpfsFile) => Promise<any>;
  onDelete?: (file: IpfsFile) => Promise<any>;
};

function File({
  name,
  size,
  file,
  downloadLoading,
  exportLoading,
  deleteLoading,
  onOpenFile,
  onDownload,
  onExport,
  onCopy,
  onDelete,
}: FileProps) {
  const ref = useRef(null);

  const { value: searchValue } = useSearch();

  const [exporStatus, setExportStatus] = useState("Export");
  const [downloadStatus, setDownloadStatus] = useState("Download");
  const [copyStatus, setCopyStatus] = useState("Copy");
  const [deleteStatus, setDeleteStatus] = useState("Delete");
  const [deleteFile, setDeleteFile] = useState<IpfsFile | null>(null);

  const file_ext = useMemo(() => {
    if (file.is_folder) return "folder";
    return (file.extension?.split(".")[1] ||
      file.name.split(".")[1] ||
      file.content_type?.split("/")[1] ||
      "folder") as FileTypes;
  }, [file.content_type, file.extension, file.is_folder, file.name]);

  const fileType = useMemo(() => {
    return extToType(file_ext);
  }, [file_ext]);

  const fileSize = useMemo(() => {
    if (size !== 0) {
      return humanizeSize(Number(size), { fractionDigits: 2 }).join("B");
    }
    return 0;
  }, [size]);

  const handleDelete = async () => {
    if (deleteFile && onDelete) {
      setDeleteStatus("Deleting...");
      try {
        await onDelete(deleteFile);
        setDeleteStatus("Deleted");
      } catch (error) {
        console.error("Delete failed", error);
      } finally {
        setTimeout(() => setDeleteStatus("Delete"), 2000);
        setDeleteFile(null);
      }
    }
  };

  return (
    <SimpleTable.Row
      ref={ref}
      onClick={(e) => (onOpenFile ? onOpenFile(file, e, fileType) : null)}
    >
      <SimpleTable.TD>
        <Flex items="center" className="gap-1">
          <FileIcon type={fileType} />
          <Tooltip content={name} className="!text-xs" place="top-start">
            <Flex className="">
              <HighlighterText
                searchValue={searchValue}
                textToHighlight={name}
                className="max-w-[110px] md:max-w-[400px] lg:max-w-[350px] xl:max-w-[400px] truncate whitespace-pre"
              />
            </Flex>
          </Tooltip>
        </Flex>
      </SimpleTable.TD>
      <SimpleTable.TD>
        {!file.is_folder && (
          <Typography.Text className="!text-xs md:!text-sm whitespace-nowrap">
            {fileSize}
          </Typography.Text>
        )}
      </SimpleTable.TD>
      <SimpleTable.TD className="w-20 gap-1">
        <div className="flex gap-2">
          <Button
            uiType="icon"
            uiSize="small"
            className="!px-1"
            onClick={(e: any) => {
              e.stopPropagation();
              if (onCopy) {
                setCopyStatus("Copying...");
                onCopy(file).then(() => {
                  setCopyStatus("Copyed");
                  setTimeout(() => setCopyStatus("Copy"), 2000);
                });
              }
            }}
            disabled={file.is_folder || file.public_cid === null}
            tooltip={{ content: copyStatus, className: "!text-xs" }}
          >
            <CopyToClipboardIcon className="w-4 h-4 md:w-5 md:h-5" />
          </Button>

          <Button
            uiType="icon"
            uiSize="small"
            className="!px-1"
            onClick={(e: any) => {
              e.stopPropagation();
              if (onExport && !exportLoading) {
                setExportStatus("Exporting...");
                onExport(file).then(() => {
                  setExportStatus("Exported");
                  setTimeout(() => setExportStatus("Export"), 2000);
                });
              }
            }}
            tooltip={{ content: exporStatus, className: "!text-xs" }}
            // loading={exportLoading}
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4 md:w-5 md:h-5" />
          </Button>

          <Button
            uiType="icon"
            uiSize="small"
            className="!px-1"
            onClick={(e: any) => {
              e.stopPropagation();
              if (onDownload && !downloadLoading) {
                setDownloadStatus("Downloading...");
                onDownload(file).then(() => {
                  setDownloadStatus("Downloaded");
                  setTimeout(() => setDownloadStatus("Download"), 2000);
                });
              }
            }}
            tooltip={{ content: downloadStatus, className: "!text-xs" }}
            // loading={downloadLoading}
          >
            <ArrowDownCircleIcon className="w-4 h-4 md:w-5 md:h-5" />
          </Button>

          <Button
            uiType="icon"
            uiSize="small"
            className="!px-1"
            onClick={(e: any) => {
              e.stopPropagation();
              setDeleteFile(file);
            }}
            tooltip={{ content: deleteStatus, className: "!text-xs" }}
          >
            <ArchiveBoxIcon className="w-4 h-4 md:w-5 md:h-5" />
          </Button>

          {deleteFile && (
            <DeleteModal
              title={`Delete ${deleteFile.name}`}
              isOpen={true}
              onClose={() => setDeleteFile(null)}
              confirmButtonText="Confirm"
              confirmButtonType="danger"
              confirmButtonClassName="w-full"
              onConfirm={handleDelete}
              loading={deleteLoading}
            />
          )}
        </div>
      </SimpleTable.TD>
    </SimpleTable.Row>
  );
}

export default File;
